import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const OutcomeHeatmap = () => {
  const [selectedCell, setSelectedCell] = useState(null);
  const [viewMode, setViewMode] = useState('outcomes');

  const conditions = [
    'Cardiovascular',
    'Diabetes',
    'Respiratory',
    'Neurological',
    'Orthopedic',
    'Oncology',
    'Mental Health',
    'Infectious Disease'
  ];

  const demographics = ['0-18', '19-35', '36-50', '51-65', '65+'];

  // Mock outcome data (success rates as percentages)
  const outcomeData = {
    'Cardiovascular': { '0-18': 95, '19-35': 92, '36-50': 88, '51-65': 82, '65+': 76 },
    'Diabetes': { '0-18': 89, '19-35': 91, '36-50': 87, '51-65': 84, '65+': 79 },
    'Respiratory': { '0-18': 94, '19-35': 90, '36-50': 85, '51-65': 81, '65+': 74 },
    'Neurological': { '0-18': 87, '19-35': 85, '36-50': 82, '51-65': 78, '65+': 71 },
    'Orthopedic': { '0-18': 96, '19-35': 94, '36-50': 91, '51-65': 88, '65+': 83 },
    'Oncology': { '0-18': 78, '19-35': 82, '36-50': 79, '51-65': 74, '65+': 68 },
    'Mental Health': { '0-18': 85, '19-35': 88, '36-50': 86, '51-65': 83, '65+': 80 },
    'Infectious Disease': { '0-18': 93, '19-35': 91, '36-50': 89, '51-65': 86, '65+': 82 }
  };

  // Mock patient count data
  const patientCountData = {
    'Cardiovascular': { '0-18': 45, '19-35': 128, '36-50': 234, '51-65': 189, '65+': 156 },
    'Diabetes': { '0-18': 23, '19-35': 89, '36-50': 198, '51-65': 167, '65+': 134 },
    'Respiratory': { '0-18': 67, '19-35': 145, '36-50': 178, '51-65': 145, '65+': 123 },
    'Neurological': { '0-18': 34, '19-35': 78, '36-50': 123, '51-65': 98, '65+': 87 },
    'Orthopedic': { '0-18': 89, '19-35': 156, '36-50': 189, '51-65': 145, '65+': 98 },
    'Oncology': { '0-18': 12, '19-35': 45, '36-50': 89, '51-65': 78, '65+': 67 },
    'Mental Health': { '0-18': 56, '19-35': 134, '36-50': 167, '51-65': 123, '65+': 89 },
    'Infectious Disease': { '0-18': 78, '19-35': 123, '36-50': 145, '51-65': 112, '65+': 78 }
  };

  const getHeatmapColor = (value) => {
    if (value >= 90) return '#10B981'; // Green
    if (value >= 80) return '#F59E0B'; // Yellow
    if (value >= 70) return '#EF4444'; // Red
    return '#6B7280'; // Gray
  };

  const getIntensity = (value) => {
    if (value >= 90) return 1;
    if (value >= 80) return 0.8;
    if (value >= 70) return 0.6;
    return 0.4;
  };

  const handleCellClick = (condition, demographic) => {
    const cellData = {
      condition,
      demographic,
      outcome: outcomeData?.[condition]?.[demographic],
      patientCount: patientCountData?.[condition]?.[demographic],
      significance: outcomeData?.[condition]?.[demographic] >= 85 ? 'High' : 
                   outcomeData?.[condition]?.[demographic] >= 75 ? 'Medium' : 'Low'
    };
    setSelectedCell(selectedCell?.condition === condition && selectedCell?.demographic === demographic ? null : cellData);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 medical-shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Treatment Outcomes Heatmap</h3>
          <p className="text-sm text-text-secondary">Success rates across conditions and demographics</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-text-secondary">View:</label>
            <select 
              value={viewMode}
              onChange={(e) => setViewMode(e?.target?.value)}
              className="text-sm border border-border rounded px-2 py-1 bg-background"
            >
              <option value="outcomes">Success Rates</option>
              <option value="counts">Patient Counts</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Download" size={16} className="text-text-secondary cursor-pointer hover:text-primary" />
            <Icon name="Maximize2" size={16} className="text-text-secondary cursor-pointer hover:text-primary" />
          </div>
        </div>
      </div>
      {/* Heatmap */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Header */}
          <div className="grid grid-cols-6 gap-1 mb-2">
            <div className="p-2"></div>
            {demographics?.map((demo) => (
              <div key={demo} className="p-2 text-center">
                <div className="text-sm font-medium text-text-primary">{demo}</div>
                <div className="text-xs text-text-secondary">years</div>
              </div>
            ))}
          </div>

          {/* Heatmap Grid */}
          <div className="space-y-1">
            {conditions?.map((condition) => (
              <div key={condition} className="grid grid-cols-6 gap-1">
                <div className="p-3 flex items-center">
                  <span className="text-sm font-medium text-text-primary">{condition}</span>
                </div>
                {demographics?.map((demographic) => {
                  const value = viewMode === 'outcomes' ? outcomeData?.[condition]?.[demographic] : patientCountData?.[condition]?.[demographic];
                  const displayValue = viewMode === 'outcomes' ? `${value}%` : value?.toString();
                  const color = getHeatmapColor(outcomeData?.[condition]?.[demographic]);
                  const intensity = getIntensity(outcomeData?.[condition]?.[demographic]);
                  
                  return (
                    <div
                      key={`${condition}-${demographic}`}
                      className="p-3 rounded cursor-pointer medical-transition hover:scale-105 hover:shadow-md"
                      style={{
                        backgroundColor: `${color}${Math.round(intensity * 255)?.toString(16)?.padStart(2, '0')}`,
                        color: intensity > 0.6 ? 'white' : 'var(--color-text-primary)'
                      }}
                      onClick={() => handleCellClick(condition, demographic)}
                    >
                      <div className="text-center">
                        <div className="text-sm font-semibold">{displayValue}</div>
                        {viewMode === 'outcomes' && (
                          <div className="text-xs opacity-75">
                            {patientCountData?.[condition]?.[demographic]} pts
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Legend */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-text-secondary">Success Rate:</span>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10B981' }} />
              <span className="text-xs text-text-secondary">90%+</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#F59E0B' }} />
              <span className="text-xs text-text-secondary">80-89%</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#EF4444' }} />
              <span className="text-xs text-text-secondary">70-79%</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#6B7280' }} />
              <span className="text-xs text-text-secondary">&lt;70%</span>
            </div>
          </div>
        </div>
        <div className="text-xs text-text-secondary">
          Click cells for detailed statistics
        </div>
      </div>
      {/* Selected Cell Details */}
      {selectedCell && (
        <div className="mt-6 p-4 bg-muted rounded-lg border-l-4 border-primary">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-semibold text-text-primary">
                {selectedCell?.condition} - Age {selectedCell?.demographic}
              </h4>
              <p className="text-sm text-text-secondary">Detailed outcome analysis</p>
            </div>
            <button
              onClick={() => setSelectedCell(null)}
              className="text-text-secondary hover:text-text-primary"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-lg font-bold text-text-primary">{selectedCell?.outcome}%</div>
              <div className="text-sm text-text-secondary">Success Rate</div>
            </div>
            <div>
              <div className="text-lg font-bold text-text-primary">{selectedCell?.patientCount}</div>
              <div className="text-sm text-text-secondary">Total Patients</div>
            </div>
            <div>
              <div className="text-lg font-bold text-text-primary">{selectedCell?.significance}</div>
              <div className="text-sm text-text-secondary">Statistical Significance</div>
            </div>
            <div>
              <div className="text-lg font-bold text-text-primary">
                {Math.round(selectedCell?.patientCount * selectedCell?.outcome / 100)}
              </div>
              <div className="text-sm text-text-secondary">Successful Outcomes</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutcomeHeatmap;